import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useFirebaseAuth } from '@/composables/useFirebaseAuth';
import { useAuthStore } from '@/stores/auth.store';
import HomeView from '@/views/HomeView.vue';
import AppLayout from '@/layouts/AppLayout.vue';
import authRoutes from './auth.routes';
import { adminRoutes } from './admin.routes';
import demoRoutes from './demo.routes';

const routes: RouteRecordRaw[] = [
  // ─── Fan routes (AppLayout, mobile-first) ───
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'home',
        component: HomeView,
        meta: { requiresAuth: true, title: 'Home' },
      },
      {
        path: 'draw',
        name: 'draw',
        component: () => import('@/views/DrawView.vue'),
        meta: { requiresAuth: true, title: '50/50 Draw' },
      },
      {
        path: 'raffle',
        name: 'raffle',
        component: () => import('@/views/RaffleView.vue'),
        meta: { requiresAuth: true, title: 'Shirt Raffle' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { requiresAuth: true, title: 'Profile' },
      },
      {
        path: 'game',
        name: 'game',
        component: () => import('@/views/GameView.vue'),
        meta: { requiresAuth: true, title: 'Live Game' },
      },
      {
        path: 'predictions',
        name: 'predictions',
        component: () => import('@/views/PredictionsView.vue'),
        meta: { requiresAuth: true, title: 'Predictions' },
      },
      {
        path: 'fixtures',
        name: 'fixtures',
        component: () => import('@/views/FixturesView.vue'),
        meta: { requiresAuth: true, title: 'Schedule' },
      },
      {
        path: 'leaderboard',
        name: 'leaderboard',
        component: () => import('@/views/LeaderboardView.vue'),
        meta: { requiresAuth: true, title: 'Leaderboard' },
      },
      {
        path: 'leagues',
        redirect: '/leaderboard',
      },
      {
        path: 'shop',
        name: 'shop',
        component: () => import('@/views/MerchView.vue'),
        meta: { requiresAuth: true, title: 'Devils Shop' },
      },
      {
        path: 'food',
        name: 'food',
        component: () => import('@/views/FBOrderView.vue'),
        meta: { requiresAuth: true, title: 'Order Food & Drink' },
      },
      {
        path: 'auctions',
        name: 'auctions',
        component: () => import('@/views/AuctionsView.vue'),
        meta: { requiresAuth: true, title: 'Live Auctions' },
      },
      {
        path: 'fan-cam',
        name: 'fan-cam',
        component: () => import('@/views/FanCamView.vue'),
        meta: { requiresAuth: true, title: 'Fan Cam' },
      },
    ],
  },

  // ─── Matchday Control (standalone layout, mobile-first, no AdminLayout) ───
  {
    path: '/matchday-control/:gameId',
    name: 'matchday-control',
    component: () => import('@/views/admin/MatchdayControlView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['venue_staff', 'club_admin'],
      title: 'Matchday Control',
    },
  },

  // ─── Demo Control (standalone layout, mobile-first, no auth for demos) ───
  {
    path: '/admin/demo',
    name: 'admin-demo',
    component: () => import('@/views/admin/DemoControlView.vue'),
    meta: {
      title: 'Demo Control',
    },
  },

  // ─── Admin routes (AdminLayout, desktop-first) ───
  ...adminRoutes,

  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
    meta: { title: 'About' },
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/OnboardingView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Welcome to Ice Master',
    },
  },
  // Auth routes
  ...authRoutes,
  // Demo routes
  ...demoRoutes,
  // ─── Arena display screens (public, no auth, HDMI output) ───
  {
    path: '/arena/:venueId',
    name: 'arena-screen',
    component: () => import('@/views/ArenaScreenView.vue'),
    meta: { title: 'Arena Display' },
  },
  {
    path: '/arena/:venueId/winner',
    name: 'arena-winner',
    component: () => import('@/views/ArenaWinnerRevealView.vue'),
    meta: { title: 'Winner Reveal' },
  },

  // Games (full-screen, no layout wrapper)
  {
    path: '/games/shootout',
    name: 'shootout',
    component: () => import('@/views/ShootoutView.vue'),
    meta: { title: 'Puck Shootout' },
  },
  {
    path: '/games/goalie',
    name: 'goalie',
    component: () => import('@/views/GoalieView.vue'),
    meta: { requiresAuth: true, title: 'Goalie Mode' },
  },

  // ─── Fan Battle (AppLayout, auth required) ───
  {
    path: '/fan-battle',
    component: AppLayout,
    children: [
      {
        path: '',
        name: 'fan-battle',
        component: () => import('@/views/FanBattleView.vue'),
        meta: { requiresAuth: true, title: 'Fan Battle' },
      },
    ],
  },

  // 404 - Must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Navigation guards
router.beforeEach((to, from, next) => {
  const { isAuthenticated, authState } = useFirebaseAuth();

  // Wait for auth state to be determined, then re-evaluate
  if (authState.value === 'loading') {
    const checkAuth = setInterval(() => {
      if (authState.value !== 'loading') {
        clearInterval(checkAuth);
        // Re-evaluate: if route requires auth and user isn't authenticated, go to login
        const needsAuth = to.meta.requiresAuth as boolean | undefined;
        if (needsAuth && !isAuthenticated.value) {
          next({ name: 'login', query: { redirect: to.fullPath } });
        } else {
          next();
        }
      }
    }, 100);
    return;
  }

  // Check if route requires authentication
  const requiresAuth = to.meta.requiresAuth as boolean | undefined;

  // Redirect to login if not authenticated and route requires auth
  if (requiresAuth && !isAuthenticated.value) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  // Redirect to home if authenticated and trying to access auth pages
  if (requiresAuth === false && isAuthenticated.value) {
    // Allow access to verify-email even when authenticated
    if (to.name === 'verify-email') {
      next();
      return;
    }

    const redirectPath = (to.query.redirect as string) || '/';
    next(redirectPath);
    return;
  }

  // Onboarding guard: redirect to /onboarding if authenticated but not onboarded
  if (isAuthenticated.value && to.name !== 'onboarding') {
    const authStore = useAuthStore();
    if (authStore.needsOnboarding) {
      next({ name: 'onboarding' });
      return;
    }
  }

  // Prevent access to /onboarding if already onboarded
  if (isAuthenticated.value && to.name === 'onboarding') {
    const authStore = useAuthStore();
    if (!authStore.needsOnboarding) {
      next('/');
      return;
    }
  }

  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} | ice-master-client`;
  }

  next();
});

export default router;
